import { useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import type { UsuarioSesion } from '@manejo-alcanza/contracts';
import { buscar, salir } from '../api';
import { ChatFlotante } from './patroclo/ChatFlotante';
import { LogoPatroclo } from './patroclo/LogoPatroclo';

/**
 * El armazón de la aplicación.
 *
 * El menú se organiza por **qué quieres hacer**, no por entidades de base de
 * datos. «Plata» y no «Facturas / Recaudos / Deducciones»: quien entra ahí va a
 * una pregunta —cuánto nos deben y por qué no cuadra—, no a una tabla.
 *
 * ## Una barra lateral, y es la misma en el teléfono
 *
 * *(Pedro, 22/10/2026: «que la barra de opciones esté a la izquierda».)*
 *
 * Antes el menú era una fila arriba, y con los once enlaces del administrador
 * solo cabía desde 1280 px. Ahora:
 *
 * - **Ancho** (`lg`): una columna fija a la izquierda, con los enlaces en grupos
 *   y el usuario abajo. Arriba de la página queda solo el buscador.
 * - **Estrecho**: el nombre y un botón. El mismo menú se abre como un cajón
 *   desde la izquierda, con objetivos de 44 px *(`docs/DISENO.md`)*.
 *
 * **No son dos menús**: los dos salen de `enlacesDe()`. Mantener dos versiones
 * sería mantener dos verdades sobre quién ve qué.
 */
export function Armazon({
  usuario,
  alSalir,
  children,
}: {
  usuario: UsuarioSesion;
  alSalir: () => void;
  children: ReactNode;
}): ReactNode {
  const [menuAbierto, setMenuAbierto] = useState(false);
  const cerrar = () => setMenuAbierto(false);
  const enlaces = enlacesDe(usuario);
  const conPatroclo = usuario.rol === 'ADMINISTRADOR' || usuario.rol === 'DESARROLLADOR';

  return (
    <div className="min-h-screen lg:pl-64">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col bg-gradient-to-b from-marino to-marino-profundo text-white lg:flex">
        <Marca />
        <div className="flex-1 overflow-y-auto px-3 pb-4">
          <Menu enlaces={enlaces} />
        </div>
        <PieDeUsuario usuario={usuario} alSalir={alSalir} />
      </aside>

      <header className="sticky top-0 z-20 border-b border-borde/70 bg-fondo/85 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2.5 lg:px-6">
          {/* Estrecho: el escudo y el nombre, porque aquí no hay barra lateral. */}
          <Link to="/" className="flex items-center gap-2 lg:hidden">
            <img src="/del-pastor-escudo.svg" alt="" className="h-9 w-9" />
            <span className="font-titulo text-lg font-semibold text-marino">ManejoAlcanza</span>
          </Link>

          <div className="ml-auto hidden lg:block">
            <BuscadorGlobal />
          </div>

          {/*
            Estrecho: un solo botón. `aria-expanded` y `aria-label` porque un
            icono sin texto no dice nada a quien navega con lector de pantalla.
          */}
          <button
            type="button"
            onClick={() => setMenuAbierto((abierto) => !abierto)}
            aria-expanded={menuAbierto}
            aria-label={menuAbierto ? 'Cerrar el menú' : 'Abrir el menú'}
            className="ml-auto flex h-11 w-11 items-center justify-center rounded-xl border border-borde bg-superficie text-marino shadow-suave lg:hidden"
          >
            <Hamburguesa abierto={menuAbierto} />
          </button>
        </div>
      </header>

      {menuAbierto && (
        // Encima del botón de Patroclo (z-40): el cajón tapa toda la página.
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Cerrar el menú"
            onClick={cerrar}
            className="absolute inset-0 bg-marino-profundo/50 backdrop-blur-[2px]"
          />
          <div className="aparecer absolute inset-y-0 left-0 flex w-[86%] max-w-xs flex-col bg-gradient-to-b from-marino to-marino-profundo text-white shadow-flotante">
            <div className="flex items-center justify-between pr-3">
              <Marca />
              <button
                type="button"
                onClick={cerrar}
                aria-label="Cerrar el menú"
                className="flex h-11 w-11 items-center justify-center rounded-xl text-white/80 hover:bg-white/10"
              >
                <Hamburguesa abierto />
              </button>
            </div>
            <div className="px-3 pb-3">
              <BuscadorGlobal alElegir={cerrar} />
            </div>
            <div className="flex-1 overflow-y-auto px-3 pb-4">
              {/*
                El cajón se cierra al tocar, no desde un efecto que mire la ruta.
                Es la misma consecuencia y una causa más directa: sin esto el
                menú se queda abierto tapando la página a la que acabas de ir.
              */}
              <Menu enlaces={enlaces} alTocar={cerrar} grande />
            </div>
            <PieDeUsuario usuario={usuario} alSalir={alSalir} alTocar={cerrar} />
          </div>
        </div>
      )}

      <main className="mx-auto max-w-7xl p-4 lg:p-6">{children}</main>

      {/*
        Patroclo a mano desde cualquier pantalla. Los mismos roles que su
        pestaña y que el proxy: esconderlo es comodidad, la autorización de
        verdad está en la API y en Patroclo.
      */}
      {conPatroclo && <ChatFlotante />}
    </div>
  );
}

type Grupo = 'Trabajo diario' | 'Entregas' | 'Plata' | 'Sistema';

/** El orden en que se ven los grupos. Uno sin enlaces para el rol no se pinta. */
const GRUPOS: Grupo[] = ['Trabajo diario', 'Entregas', 'Plata', 'Sistema'];

interface EnlaceMenu {
  a: string;
  texto: string;
  grupo: Grupo;
}

/**
 * Qué entradas ve cada rol.
 *
 * Vive en una función y no repartido por el JSX porque **la barra ancha y el
 * cajón del teléfono tienen que mostrar exactamente lo mismo**. Duplicar las
 * condiciones era garantizar que algún día se separaran.
 *
 * Y esto **no es la autorización**: el backend responde 403 igual. Es que un
 * enlace que lleva a un 403 es peor que no tenerlo.
 */
function enlacesDe(usuario: UsuarioSesion): EnlaceMenu[] {
  const rol = usuario.rol;
  const enlaces: EnlaceMenu[] = [
    { a: '/ordenes', texto: 'Órdenes', grupo: 'Trabajo diario' },
    { a: '/cargar', texto: 'Cargar órdenes', grupo: 'Trabajo diario' },
  ];

  // El CAU defiende una entrega ante el cliente: es territorio comercial, y el
  // contador no toca entregas.
  if (rol === 'ADMINISTRADOR' || rol === 'COMERCIAL_FINANCIERO' || rol === 'DESARROLLADOR') {
    enlaces.push({ a: '/cau', texto: 'CAU', grupo: 'Entregas' });
  }
  if (rol !== 'REPARTIDOR' && rol !== 'PRODUCTOR') {
    enlaces.push({ a: '/plata', texto: 'Plata', grupo: 'Plata' });
    // F8. El contador entra: es lectura financiera, y los cierres mensuales son
    // suyos. El productor y el repartidor no tienen nada que hacer aqui.
    enlaces.push({ a: '/cierre', texto: 'Cierre de mes', grupo: 'Plata' });
  }
  if (rol !== 'PRODUCTOR' && rol !== 'CONTADOR') {
    enlaces.push(
      { a: '/alistar', texto: 'Alistar', grupo: 'Entregas' },
      { a: '/camion', texto: 'Camión', grupo: 'Entregas' },
    );
  }
  if (
    rol === 'ADMINISTRADOR' ||
    rol === 'PRODUCTOR' ||
    rol === 'REPARTIDOR' ||
    rol === 'DESARROLLADOR'
  ) {
    enlaces.push({ a: '/inventario', texto: 'Inventario', grupo: 'Entregas' });
  }
  if (rol === 'ADMINISTRADOR' || rol === 'DESARROLLADOR') {
    // Patroclo: los mismos roles que el proxy. Esconderlo es comodidad; la
    // autorizacion de verdad esta en la API y en Patroclo.
    enlaces.push(
      { a: '/patroclo', texto: 'Patroclo', grupo: 'Sistema' },
      { a: '/gastos', texto: 'Gastos', grupo: 'Sistema' },
    );
  }
  if (rol === 'ADMINISTRADOR') {
    enlaces.push({ a: '/usuarios', texto: 'Usuarios', grupo: 'Sistema' });
  }

  return enlaces;
}

/** El escudo de Del Pastor y el nombre del sistema, arriba de la barra. */
function Marca(): ReactNode {
  return (
    <Link to="/" className="flex items-center gap-3 px-5 pb-5 pt-6">
      <img src="/del-pastor-escudo.svg" alt="" className="h-11 w-11 shrink-0 drop-shadow" />
      <span className="leading-tight">
        <span className="block font-titulo text-lg font-semibold">ManejoAlcanza</span>
        <span className="block text-[0.7rem] uppercase tracking-[0.14em] text-lima/90">Del Pastor</span>
      </span>
    </Link>
  );
}

function Menu({
  enlaces,
  alTocar,
  grande = false,
}: {
  enlaces: EnlaceMenu[];
  alTocar?: () => void;
  grande?: boolean;
}): ReactNode {
  return (
    <nav className="space-y-5">
      {GRUPOS.map((grupo) => {
        const delGrupo = enlaces.filter((e) => e.grupo === grupo);
        if (delGrupo.length === 0) return null;
        return (
          <div key={grupo}>
            <div className="px-3 pb-1.5 text-[0.68rem] font-semibold uppercase tracking-[0.14em] text-white/45">
              {grupo}
            </div>
            <ul className="space-y-0.5">
              {delGrupo.map((e) => (
                <li key={e.a}>
                  <Enlace a={e.a} alTocar={alTocar} grande={grande}>
                    {e.texto}
                  </Enlace>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

function Enlace({
  a,
  children,
  alTocar,
  grande,
}: {
  a: string;
  children: ReactNode;
  alTocar?: () => void;
  grande: boolean;
}): ReactNode {
  return (
    <NavLink
      to={a}
      onClick={alTocar}
      className={({ isActive }) =>
        // En el cajón del teléfono, el tamaño que necesita un dedo (`docs/DISENO.md`).
        `group relative flex items-center gap-3 rounded-lg px-3 transition ${
          grande ? 'min-h-[44px] text-base' : 'py-2 text-sm'
        } ${
          isActive
            ? 'bg-white/12 font-medium text-white before:absolute before:inset-y-2 before:left-0 before:w-1 before:rounded-r before:bg-lima'
            : 'text-white/70 hover:bg-white/8 hover:text-white'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span className={isActive ? 'text-lima' : 'text-white/55 group-hover:text-white/85'}>
            <Icono a={a} />
          </span>
          {children}
        </>
      )}
    </NavLink>
  );
}

/** El usuario, su contraseña y la salida: abajo de la barra, donde no estorban. */
function PieDeUsuario({
  usuario,
  alSalir,
  alTocar,
}: {
  usuario: UsuarioSesion;
  alSalir: () => void;
  alTocar?: () => void;
}): ReactNode {
  return (
    <div className="border-t border-white/10 p-3">
      <div className="flex items-center gap-3 px-2 py-2">
        <span
          aria-hidden="true"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-lima font-semibold text-marino-profundo"
        >
          {usuario.nombre.trim().charAt(0).toUpperCase()}
        </span>
        <QuienSoy usuario={usuario} />
      </div>
      <div className="mt-1 grid grid-cols-2 gap-2">
        <Link
          to="/clave"
          onClick={alTocar}
          className="flex min-h-[40px] items-center justify-center rounded-lg bg-white/8 px-2 text-xs text-white/85 hover:bg-white/15"
        >
          Mi contraseña
        </Link>
        <button
          onClick={() => void salir().then(alSalir)}
          className="min-h-[40px] rounded-lg bg-white/8 px-2 text-xs text-white/85 hover:bg-white/15"
        >
          Salir
        </button>
      </div>
    </div>
  );
}

/**
 * El rol y, para los operativos, su ubicación.
 *
 * El rol dice qué puedes hacer; la ubicación, sobre qué.
 */
function QuienSoy({ usuario }: { usuario: UsuarioSesion }): ReactNode {
  return (
    <div className="min-w-0 text-xs">
      <div className="truncate font-medium text-white">{usuario.nombre}</div>
      <div className="truncate text-white/55">
        {usuario.rol.replaceAll('_', ' ').toLowerCase()}
        {usuario.ubicacionAsignada && ` · ${usuario.ubicacionAsignada.toLowerCase()}`}
      </div>
    </div>
  );
}

/** Tres rayas que se vuelven una equis. Dibujadas, no una fuente de iconos. */
function Hamburguesa({ abierto }: { abierto: boolean }): ReactNode {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
      {abierto ? (
        <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="5" y1="5" x2="17" y2="17" />
          <line x1="17" y1="5" x2="5" y2="17" />
        </g>
      ) : (
        <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="4" y1="6" x2="18" y2="6" />
          <line x1="4" y1="11" x2="18" y2="11" />
          <line x1="4" y1="16" x2="18" y2="16" />
        </g>
      )}
    </svg>
  );
}

/** Un icono por entrada del menú. Dibujados aquí: una fuente de iconos sería un recurso externo más. */
const TRAZOS: Record<string, ReactNode> = {
  '/ordenes': (
    <>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4h6v3H9zM9 11h6M9 15h4" />
    </>
  ),
  '/cargar': <path d="M12 15V4M7.5 8.5 12 4l4.5 4.5M5 15v3a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-3" />,
  '/cau': <path d="M12 3l7 3v6c0 4.2-3 7.4-7 9-4-1.6-7-4.8-7-9V6l7-3zM9 12l2 2 4-4" />,
  '/plata': (
    <>
      <rect x="3" y="6" width="18" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6.5 9.5v.01M17.5 14.5v.01" />
    </>
  ),
  '/cierre': (
    <>
      <rect x="4" y="5" width="16" height="15" rx="2" />
      <path d="M4 10h16M9 3v4M15 3v4M9.5 15l2 2 3.5-3.5" />
    </>
  ),
  '/alistar': <path d="M3.5 7.5 12 3.5l8.5 4-8.5 4-8.5-4zM3.5 7.5v9l8.5 4 8.5-4v-9M12 11.5v9" />,
  '/camion': (
    <>
      <path d="M2.5 6.5h11v9.5h-11zM13.5 9.5h4l3 3.2V16h-7" />
      <circle cx="6.5" cy="17.5" r="1.8" />
      <circle cx="17" cy="17.5" r="1.8" />
    </>
  ),
  '/inventario': <path d="M12 3.5 20.5 8 12 12.5 3.5 8 12 3.5zM3.5 12 12 16.5 20.5 12M3.5 16 12 20.5 20.5 16" />,
  '/gastos': <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3zM9 8h6M9 12h6M9 16h3" />,
  '/usuarios': (
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M15.5 4.6a3 3 0 0 1 0 6M17.5 14.2c2 .7 3.5 2.8 3.5 5.8" />
    </>
  ),
};

function Icono({ a }: { a: string }): ReactNode {
  if (a === '/patroclo') return <LogoPatroclo variante="linea" className="h-5 w-5" />;
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {TRAZOS[a]}
    </svg>
  );
}

/**
 * El buscador global.
 *
 * Pegar `0858622956` en cualquier momento y llegar ahí vale más que tres
 * menús.
 */
function BuscadorGlobal({ alElegir }: { alElegir?: () => void } = {}): ReactNode {
  const [q, setQ] = useState('');
  const navegar = useNavigate();

  /*
   * Se espera a que dejes de escribir antes de preguntar.
   *
   * Sin esto, cada tecla es una peticion: escribir «0858622956» son ocho
   * consultas de las que solo importa la ultima, y con el limite de peticiones
   * puesto las ultimas llegan a rebotar con 429.
   */
  const [qDiferido, setQDiferido] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQDiferido(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const resultados = useQuery({
    queryKey: ['buscar', qDiferido],
    queryFn: () => buscar(qDiferido),
    enabled: qDiferido.length >= 3,
  });

  return (
    <div className="relative">
      <svg
        viewBox="0 0 24 24"
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tenue"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="6.5" />
        <path d="m20 20-4.2-4.2" />
      </svg>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar una orden…"
        aria-label="Buscar una orden"
        // Ancho completo en el cajón del teléfono; fijo cuando va arriba.
        className="h-11 w-full rounded-full border border-borde pl-9 pr-3 text-base text-texto shadow-suave lg:h-10 lg:w-72 lg:text-sm"
      />
      {qDiferido.length >= 3 && resultados.data && resultados.data.ordenes.length > 0 && (
        <ul className="absolute right-0 z-10 mt-2 w-full overflow-hidden rounded-xl border border-borde bg-superficie text-texto shadow-flotante lg:w-80">
          {resultados.data.ordenes.map((o) => (
            <li key={o.id}>
              <button
                onClick={() => {
                  // `navigate` devuelve una promesa desde React Router 7; el
                  // `void` dice que no se espera a proposito.
                  void navegar(`/ordenes/${o.id}`);
                  setQ('');
                  alElegir?.();
                }}
                className="block w-full px-4 py-2.5 text-left text-sm hover:bg-acento-tenue"
              >
                <span className="font-medium text-marino">{o.numero}</span>
                <span className="block text-xs text-tenue">
                  {o.clienteNombre} · {o.estado.toLowerCase()}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
